/**
 * TeamSwitcher
 *
 * Dropdown that lists all active team memberships for the current user
 * and allows switching the active team via setActiveTeamId.
 *
 * Contract: UI Team Authorization Contract V1.1 §8
 * - Deactivated teams shown as disabled rows (not selectable).
 * - Disabled memberships excluded entirely.
 * - If activeTeamId is invalid (deactivated/missing/disabled), auto-resolves:
 *   a) Switch to first valid team if available
 *   b) Navigate to /team-picker if no valid teams remain
 *
 * Only rendered when the user has more than one membership entry (active or deactivated).
 * Single-team users see no switcher (no clutter).
 */

import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { ChevronsUpDown, Check, Building2, Ban } from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import { useTeam } from '@/contexts/TeamContext';
import { useAuth } from '@/contexts/AuthContext';
import { getUserAllMemberships, type MembershipIndexDoc } from '@/services/membershipIndex.service';
import { doc, getDoc } from 'firebase/firestore';
import { db } from '@/lib/firebase';
import { logger } from '@/lib/logger';

interface VerifiedMembership {
  index: MembershipIndexDoc;
  /** True if member.active=true AND team.isActive !== false */
  isSelectable: boolean;
  /** True if team.isActive === false (team-level deactivation) */
  isTeamDeactivated: boolean;
}

export function TeamSwitcher() {
  const { t } = useTranslation('common');
  const { user } = useAuth();
  const { activeTeamId, team, setActiveTeamId, loading } = useTeam();
  const navigate = useNavigate();
  const [memberships, setMemberships] = useState<VerifiedMembership[]>([]);
  const [switching, setSwitching] = useState(false);

  useEffect(() => {
    if (!user) {
      setMemberships([]);
      return;
    }

    getUserAllMemberships(user.uid)
      .then(async (all) => {
        // §8.2: Cross-check canonical member doc AND team doc.
        // Index is a performance layer only — authority comes from canonical docs.
        const verified = await Promise.all(
          all.map(async (m): Promise<VerifiedMembership | null> => {
            try {
              // 1. Check canonical member doc
              const memberSnap = await getDoc(doc(db, 'teams', m.teamId, 'members', user.uid));
              if (!memberSnap.exists()) return null;
              const memberData = memberSnap.data();
              if (memberData.active === false) {
                // Member disabled — exclude entirely (not even shown as disabled)
                return null;
              }

              // 2. Check canonical team doc for isActive
              let isTeamDeactivated = false;
              try {
                const teamSnap = await getDoc(doc(db, 'teams', m.teamId));
                if (!teamSnap.exists()) return null;
                const teamData = teamSnap.data();
                // isActive defaults to true for legacy teams without the field
                isTeamDeactivated = teamData.isActive === false;
              } catch {
                // If team doc is unreadable, treat as deactivated
                isTeamDeactivated = true;
              }

              return {
                index: m,
                isSelectable: !isTeamDeactivated,
                isTeamDeactivated,
              };
            } catch {
              // If member doc is unreadable, exclude
              return null;
            }
          })
        );

        const validEntries = verified.filter((m): m is VerifiedMembership => m !== null);
        setMemberships(validEntries);

        // §8.4: If the current activeTeamId is invalid, auto-resolve.
        const activeIsValid = validEntries.some(
          (m) => m.index.teamId === activeTeamId && m.isSelectable
        );

        if (activeTeamId && !activeIsValid) {
          logger.warn('[TeamSwitcher] activeTeamId is invalid, auto-resolving', { activeTeamId });

          // Try to switch to first selectable team
          const firstValid = validEntries.find((m) => m.isSelectable);
          if (firstValid) {
            logger.info('[TeamSwitcher] Auto-switching to first valid team', {
              teamId: firstValid.index.teamId,
            });
            try {
              await setActiveTeamId(firstValid.index.teamId);
            } catch (err) {
              logger.error('[TeamSwitcher] Auto-switch failed', err);
              navigate('/team-picker', { replace: true });
            }
          } else {
            // No valid teams — go to team picker
            logger.warn('[TeamSwitcher] No valid teams available, navigating to team-picker');
            navigate('/team-picker', { replace: true });
          }
        }
      })
      .catch((err) => {
        logger.warn('[TeamSwitcher] Failed to load memberships', err);
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, activeTeamId]);

  // Only render when user has multiple membership entries
  if (memberships.length <= 1 || loading) {
    return null;
  }

  const handleSwitch = async (teamId: string) => {
    if (teamId === activeTeamId || switching) return;
    setSwitching(true);
    try {
      await setActiveTeamId(teamId);
    } catch (err) {
      logger.error('[TeamSwitcher] Failed to switch team', err);
    } finally {
      setSwitching(false);
    }
  };

  const currentTeamName = team?.name ?? '…';

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          variant="ghost"
          size="sm"
          className={cn(
            'w-full justify-between px-2 h-8 text-sm font-medium',
            'text-sidebar-foreground hover:bg-sidebar-accent hover:text-sidebar-accent-foreground',
            switching && 'opacity-60 pointer-events-none'
          )}
          aria-label="Switch team"
        >
          <span className="flex items-center gap-2 min-w-0">
            <Building2 className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
            <span className="truncate">{currentTeamName}</span>
          </span>
          <ChevronsUpDown className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
        </Button>
      </DropdownMenuTrigger>

      <DropdownMenuContent align="start" className="w-56">
        <DropdownMenuLabel className="text-xs text-muted-foreground font-normal">
          {t('teamSwitcher.switchTeam')}
        </DropdownMenuLabel>
        <DropdownMenuSeparator />
        {memberships.map(({ index: m, isSelectable, isTeamDeactivated }) => (
          <DropdownMenuItem
            key={m.teamId}
            onSelect={() => {
              if (isSelectable) void handleSwitch(m.teamId);
            }}
            disabled={!isSelectable}
            className={cn(
              'flex items-center gap-2',
              isSelectable ? 'cursor-pointer' : 'cursor-not-allowed opacity-50'
            )}
          >
            {isTeamDeactivated ? (
              <Ban className="h-3.5 w-3.5 shrink-0 text-destructive" />
            ) : (
              <Check
                className={cn(
                  'h-3.5 w-3.5 shrink-0',
                  m.teamId === activeTeamId ? 'opacity-100' : 'opacity-0'
                )}
              />
            )}
            <span className="truncate flex-1">{m.teamName ?? m.teamId}</span>
            {isTeamDeactivated && (
              <span className="text-xs text-destructive shrink-0">
                {t('teamSwitcher.deactivated')}
              </span>
            )}
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
